import { useMutation, useQuery } from '@apollo/client'
import { Button, Grid } from '@mui/material'
import ToggleEditSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditSelectField'
import { GetCandidateLocationPreferenceOptions } from 'candidate/graphql/recruitingPreferences.gql'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateLocationPreferences } from 'shared/graphql/candidatePreferences.gql'
import { EditButtonContainer } from 'shared/style/styled'
import PreferenceSectionHeader from './PreferenceSectionHeader'
import { RecruitingFormComponentProps } from './preference.types'

const Location: React.FC<RecruitingFormComponentProps> = ({
    candidate,
    handleEditArray,
    editArray,
    sectionName,
    setUnsavedChangesExist,
    refetch,
}) => {
    const [mutate] = useMutation(UpdateCandidateLocationPreferences)
    const toast = useToast()
    const locationOptions = useQuery(GetCandidateLocationPreferenceOptions)
    const locationPreferences = useMemo(
        () => candidate.locationPreferences ?? [],
        [candidate]
    )

    const [selectedPrimaryLocation, setSelectedPrimaryLocation] = useState(
        locationPreferences.find((location) => location.order === 1)
            ?.preferenceLocation
    )
    const [selectedSecondaryLocation, setSelectedSecondaryLocation] = useState(
        locationPreferences.find((location) => location.order === 2)
            ?.preferenceLocation
    )
    const [selectedTertiaryLocation, setSelectedTertiaryLocation] = useState(
        locationPreferences.find((location) => location.order === 3)
            ?.preferenceLocation
    )

    const isEdit = useMemo(
        () =>
            editArray.find((ele) => ele.sectionName === sectionName)?.isEdit ||
            false,
        [editArray, sectionName]
    )

    const resetForm = useCallback(() => {
        setSelectedPrimaryLocation(
            locationPreferences.find((location) => location.order === 1)
                ?.preferenceLocation
        )
        setSelectedSecondaryLocation(
            locationPreferences.find((location) => location.order === 2)
                ?.preferenceLocation
        )
        setSelectedTertiaryLocation(
            locationPreferences.find((location) => location.order === 3)
                ?.preferenceLocation
        )
    }, [
        locationPreferences,
        setSelectedPrimaryLocation,
        setSelectedSecondaryLocation,
        setSelectedTertiaryLocation,
    ])

    // update formValues state if new data comes back from the API
    useEffect(() => {
        resetForm()
    }, [locationPreferences, resetForm])

    const unsavedChangesExist = useMemo(() => {
        if (!locationPreferences) return false
        const primaryFromDb = locationPreferences.find(
            ({ order }) => order === 1
        )
        const secondaryFromDb = locationPreferences.find(
            ({ order }) => order === 2
        )
        const tertiaryFromDb = locationPreferences.find(
            ({ order }) => order === 3
        )
        if (
            selectedPrimaryLocation?.id !==
                primaryFromDb?.preferenceLocationId ||
            selectedSecondaryLocation?.id !==
                secondaryFromDb?.preferenceLocationId ||
            selectedTertiaryLocation?.id !==
                tertiaryFromDb?.preferenceLocationId
        ) {
            return true
        } else {
            return false
        }
    }, [
        locationPreferences,
        selectedPrimaryLocation,
        selectedSecondaryLocation,
        selectedTertiaryLocation,
    ])

    useEffect(
        () => setUnsavedChangesExist(unsavedChangesExist),
        [unsavedChangesExist, setUnsavedChangesExist]
    )

    // reset form when we cancel out of editing
    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const handleUpdate = async () => {
        try {
            const { data: mutationResult, errors } = await mutate({
                variables: {
                    candidateId: candidate.id,
                    preferences: [
                        {
                            preferenceLocationId: selectedPrimaryLocation?.id,
                            order: 1,
                        },
                        {
                            preferenceLocationId: selectedSecondaryLocation?.id,
                            order: 2,
                        },
                        {
                            preferenceLocationId: selectedTertiaryLocation?.id,
                            order: 3,
                        },
                    ].filter((pref) => !!pref.preferenceLocationId),
                },
            })
            if (mutationResult.updateCandidateLocationPreferences.success) {
                handleEditArray(sectionName, true)
                toast({
                    title: 'Success',
                    message: 'Your location preferences have been saved',
                    severity: 'success',
                })
                refetch()
                setUnsavedChangesExist(false)
            } else {
                console.log(errors)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: 'Failed to update location preferences',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update location preferences',
            })
        }
        handleEditArray(sectionName, true)
    }
    return (
        <>
            <PreferenceSectionHeader
                handleEditArray={handleEditArray}
                sectionName={sectionName}
                title="Location"
                subTitle="Where are your preferred working locations?"
            />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <ToggleEditSelectField
                        edit={isEdit}
                        label="Primary Preference"
                        labelId="location_primary_preference"
                        disabled={false}
                        dropdownData={
                            locationOptions.data?.preferenceLocations.filter(
                                (option) =>
                                    selectedSecondaryLocation?.id !==
                                        option.id &&
                                    selectedTertiaryLocation?.id !== option.id
                            ) || []
                        }
                        selectedDropdownData={selectedPrimaryLocation}
                        onChange={(e) => {
                            setSelectedPrimaryLocation(
                                locationOptions.data?.preferenceLocations.find(
                                    ({ id }) => id === e.target.value
                                )
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ToggleEditSelectField
                        edit={isEdit}
                        label="Secondary Preference"
                        labelId="location_secondary_reference"
                        disabled={false}
                        dropdownData={
                            locationOptions.data?.preferenceLocations.filter(
                                (option) =>
                                    selectedPrimaryLocation?.id !== option.id &&
                                    selectedTertiaryLocation?.id !== option.id
                            ) || []
                        }
                        selectedDropdownData={selectedSecondaryLocation}
                        onChange={(e) => {
                            setSelectedSecondaryLocation(
                                locationOptions.data?.preferenceLocations.find(
                                    ({ id }) => id === e.target.value
                                )
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ToggleEditSelectField
                        edit={isEdit}
                        label="Tertiary Preference"
                        labelId="location_tertiary_preference"
                        disabled={false}
                        dropdownData={
                            locationOptions.data?.preferenceLocations.filter(
                                (option) =>
                                    selectedSecondaryLocation?.id !==
                                        option.id &&
                                    selectedPrimaryLocation?.id !== option.id
                            ) || []
                        }
                        selectedDropdownData={selectedTertiaryLocation}
                        onChange={(e) => {
                            setSelectedTertiaryLocation(
                                locationOptions.data?.preferenceLocations.find(
                                    ({ id }) => id === e.target.value
                                )
                            )
                        }}
                    />
                </Grid>
                {isEdit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleEditArray(sectionName, true)
                                resetForm()
                                setUnsavedChangesExist(false)
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate}>Save and Update</Button>
                    </EditButtonContainer>
                )}
            </Grid>
        </>
    )
}

export default Location
